import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    CircularProgress,
    InputAdornment, // Para añadir el símbolo de moneda
    Typography,
} from "@mui/material";
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'; // Para fechas y horas
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

// Hook para obtener los tipos de mantenimiento
import { useComprobanteTypes } from "../../../hooks/accounting/useComprobanteTypes"; // Asumo que este hook trae Tipos de Mantenimiento
// Nota: El nombre 'useComprobanteTypes' sugiere tipos de comprobante.
// Si tienes un hook para 'TIPO_MANTENIMIENTOS', deberías usarlo aquí.
import { useMaintenanceTypes } from "../../../hooks/maintenance/useMaintenanceTypes";

// Esquema de validación con Yup
const maintenanceSchema = yup.object().shape({
    // comid no se valida aquí porque viene del contexto y no es editable directamente
    tipmanid: yup.number()
        .required("El tipo de mantenimiento es obligatorio.")
        .positive("Seleccione un tipo de mantenimiento válido."),
    manfecini: yup.date()
        .required("La fecha y hora de inicio son obligatorias.")
        .typeError("Formato de fecha y hora de inicio inválido."),
        //.max(new Date(), "La fecha de inicio no puede ser futura."),
    manfecfin: yup.date()
        .nullable()
        .typeError("Formato de fecha y hora de fin inválido.")
        .test(
            'is-after-start',
            'La fecha de fin no puede ser anterior a la fecha de inicio.',
            function (value) {
                const { manfecini } = this.parent;
                return !manfecini || !value || value >= manfecini;
            }
        ),
    manest: yup.string()
        .required("El estado es obligatorio.")
        .oneOf(['CREADO', 'INICIADO', 'EN_PROCESO', 'FINALIZADO', 'CANCELADO'], "Estado inválido."),
    mankm: yup.number()
        .nullable()
        .min(0, "El kilometraje no puede ser negativo.")
        .typeError("El kilometraje debe ser un número."),
    manobs: yup.string()
        .nullable()
        .max(255, "Las observaciones no pueden exceder 255 caracteres."),
    mantiefuehor: yup.number()
        .nullable()
        .min(0, "El tiempo fuera de horas no puede ser negativo.")
        .integer("El tiempo fuera de horas debe ser un número entero.")
        .typeError("El tiempo fuera de horas debe ser un número."),
    costototal: yup.number()
        .nullable()
        .min(0, "El costo total no puede ser negativo.")
        .typeError("El costo total debe ser un número."),
});

const MaintenanceForm = ({ open, handleClose, onSubmit, defaultValues }) => {
    // Cargar los tipos de mantenimiento para el selector
    // IMPORTANTE: Si 'useComprobanteTypes' no trae 'TIPO_MANTENIMIENTOS', debes cambiar este hook
    //const { comprobanteTypes: maintenanceTypes, isLoading: isLoadingMaintenanceTypes, error: errorMaintenanceTypes } = useComprobanteTypes();

    const { maintenanceTypes, isLoading: isLoadingMaintenanceTypes, error: errorMaintenanceTypes } = useMaintenanceTypes();

    
    const {
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(maintenanceSchema),
        defaultValues: {
            // ComID se gestiona desde el Dashboard y se pasa como defaultValues.comid
            tipmanid: defaultValues.tipmanid || null,
            manfecini: defaultValues.manfecini ? new Date(defaultValues.manfecini) : null,
            manfecfin: defaultValues.manfecfin ? new Date(defaultValues.manfecfin) : null,
            manest: defaultValues.manest || "CREADO",
            mankm: defaultValues.mankm || null,
            manobs: defaultValues.manobs || "",
            mantiefuehor: defaultValues.mantiefuehor || null,
            costototal: defaultValues.costototal || null,
        },
    });

    // Resetear el formulario cuando se abre o los defaultValues cambian (para modo edición)
    useEffect(() => {
        reset({
            tipmanid: defaultValues.tipmanid || null,
            manfecini: defaultValues.manfecini ? new Date(defaultValues.manfecini) : null,
            manfecfin: defaultValues.manfecfin ? new Date(defaultValues.manfecfin) : null,
            manest: defaultValues.manest || "CREADO",
            mankm: defaultValues.mankm || null,
            manobs: defaultValues.manobs || "",
            mantiefuehor: defaultValues.mantiefuehor || null,
            costototal: defaultValues.costototal || null,
        });
    }, [defaultValues, reset]);

    const handleFormSubmit = (data) => {
        // Añadir comid al payload, ya que no es un campo de formulario editable pero es necesario.
        // También se asegura de que manfecini y manfecfin sean Date objects si no se convirtió antes
        // y se le pasa al onSubmit del dashboard. El dashboard es quien convierte a ISO string.
        const payload = {
            ...data,
            comid: defaultValues.comid,
            // Asegurarse de que los números se conviertan de string a number si el input es type="number"
            // y react-hook-form no lo hace automáticamente con yup.
            mankm: data.mankm !== null && data.mankm !== '' ? Number(data.mankm) : null,
            mantiefuehor: data.mantiefuehor !== null && data.mantiefuehor !== '' ? Number(data.mantiefuehor) : null,
            costototal: data.costototal !== null && data.costototal !== '' ? Number(data.costototal) : null,
        };
        onSubmit(payload);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
                <DialogTitle>{defaultValues.manid ? "Editar Mantenimiento" : "Crear Mantenimiento"}</DialogTitle>
                <DialogContent dividers>
                    <Box component="form" noValidate sx={{ mt: 1 }}>
                        {/* Información del Componente y Vehículo (solo lectura) */}
                        <TextField
                            margin="dense"
                            label="Componente"
                            value={`${defaultValues.componentenombre || 'N/A'} (Vehículo: ${defaultValues.vehiculomatricula || 'N/A'})`}
                            fullWidth
                            variant="outlined"
                            disabled
                            sx={{ mb: 2 }}
                        />

                        {/* Selector de Tipo de Mantenimiento */}
                        {isLoadingMaintenanceTypes ? (
                            <CircularProgress size={24} sx={{ mb: 2 }} />
                        ) : errorMaintenanceTypes ? (
                            <Typography color="error" sx={{ mb: 2 }}>Error al cargar tipos de mantenimiento: {errorMaintenanceTypes.message}</Typography>
                        ) : (
                            <Controller
                                name="tipmanid"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth margin="dense" error={!!errors.tipmanid} sx={{ mb: 2 }}>
                                        <InputLabel id="tip-man-id-label">Tipo de Mantenimiento</InputLabel>
                                        <Select
                                            labelId="tip-man-id-label"
                                            label="Tipo de Mantenimiento"
                                            {...field}
                                            value={field.value || ""}
                                        >
                                            <MenuItem value="">
                                                <em>Ninguno</em>
                                            </MenuItem>
                                            {maintenanceTypes.map((type) => (
                                                <MenuItem key={type.tipmanid} value={type.tipmanid}>
                                                    {type.tipmannom}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {errors.tipmanid && <FormHelperText>{errors.tipmanid.message}</FormHelperText>}
                                    </FormControl>
                                )}
                            />
                        )}

                        {/* Campo Fecha y Hora de Inicio */}
                        <Controller
                            name="manfecini"
                            control={control}
                            render={({ field: { onChange, value, ...field } }) => (
                                <DateTimePicker
                                    {...field}
                                    label="Fecha y Hora de Inicio"
                                    inputFormat="dd/MM/yyyy HH:mm"
                                    value={value}
                                    onChange={(newValue) => onChange(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            margin="dense"
                                            fullWidth
                                            error={!!errors.manfecini}
                                            helperText={errors.manfecini?.message}
                                        />
                                    )}
                                />
                            )}
                        />

                        {/* Campo Fecha y Hora de Fin */}
                        <Controller
                            name="manfecfin"
                            control={control}
                            render={({ field: { onChange, value, ...field } }) => (
                                <DateTimePicker
                                    {...field}
                                    label="Fecha y Hora de Fin (Opcional)"
                                    inputFormat="dd/MM/yyyy HH:mm"
                                    value={value}
                                    onChange={(newValue) => onChange(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            margin="dense"
                                            fullWidth
                                            error={!!errors.manfecfin}
                                            helperText={errors.manfecfin?.message}
                                        />
                                    )}
                                />
                            )}
                        />

                        {/* Campo Estado del Mantenimiento */}
                        <Controller
                            name="manest"
                            control={control}
                            render={({ field }) => (
                                <FormControl fullWidth margin="dense" error={!!errors.manest}>
                                    <InputLabel id="man-est-label">Estado del Mantenimiento</InputLabel>
                                    <Select
                                        labelId="man-est-label"
                                        label="Estado del Mantenimiento"
                                        {...field}
                                    >
                                        <MenuItem value="CREADO">CREADO</MenuItem>
                                        <MenuItem value="INICIADO">INICIADO</MenuItem>
                                        <MenuItem value="EN_PROCESO">EN_PROCESO</MenuItem>
                                        <MenuItem value="FINALIZADO">FINALIZADO</MenuItem>
                                        <MenuItem value="CANCELADO">CANCELADO</MenuItem>
                                    </Select>
                                    {errors.manest && <FormHelperText>{errors.manest.message}</FormHelperText>}
                                </FormControl>
                            )}
                        />

                        {/* Campo Kilometraje del Mantenimiento */}
                        <Controller
                            name="mankm"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    margin="dense"
                                    label="Kilometraje del Mantenimiento (Opcional)"
                                    type="number"
                                    fullWidth
                                    variant="outlined"
                                    error={!!errors.mankm}
                                    helperText={errors.mankm?.message}
                                    inputProps={{ min: 0 }}
                                />
                            )}
                        />

                        {/* Campo Observaciones */}
                        <Controller
                            name="manobs"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    margin="dense"
                                    label="Observaciones (Opcional)"
                                    type="text"
                                    fullWidth
                                    variant="outlined"
                                    multiline
                                    rows={3}
                                    error={!!errors.manobs}
                                    helperText={errors.manobs?.message}
                                />
                            )}
                        />

                        {/* Campo Tiempo Fuera de Horas */}
                        <Controller
                            name="mantiefuehor"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    margin="dense"
                                    label="Tiempo Fuera de Horas (Opcional)"
                                    type="number"
                                    fullWidth
                                    variant="outlined"
                                    error={!!errors.mantiefuehor}
                                    helperText={errors.mantiefuehor?.message}
                                    inputProps={{ min: 0 }}
                                />
                            )}
                        />

                        {/* Campo Costo Total */}
                        <Controller
                            name="costototal"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    margin="dense"
                                    label="Costo Total (Opcional)"
                                    type="number"
                                    fullWidth
                                    variant="outlined"
                                    error={!!errors.costototal}
                                    helperText={errors.costototal?.message}
                                    inputProps={{ step: "0.01", min: 0 }} // Para permitir decimales y no negativos
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    }}
                                />
                            )}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="inherit">
                        Cancelar
                    </Button>
                    <Button onClick={handleSubmit(handleFormSubmit)} variant="contained" color="primary">
                        {defaultValues.manid ? "Guardar Cambios" : "Crear Mantenimiento"}
                    </Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};

export default MaintenanceForm;